import React, { useEffect, useState, useContext } from 'react';
import {
	Grid,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	useMediaQuery,
	Backdrop,
	CircularProgress,
	Alert,
} from '@mui/material';
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from '@mui/material/styles';

import { CountryList as BulkPurchaseCountryList } from '@components/BulkPurchaseCountryList';
import { CurrencyList } from '@components/BulkPurchaseCurrencyList';
import { CountryList } from '@components/CountryList';
import Input from '@components/Input';
import SkuList from '@components/SkuList';
import { getProducts as getAllProducts } from '@store/sort-sku-management';
import BulkPurchaseAuth from './bulkPurchaseAuth';

import { addBulkPurchase } from "@store/bulk-purchase-management";
import DashboardEnvContext from "../../../../DashboardApp/DashboardEnvContext";


const ALL_CATEGORY = 'all';

const SSO_ACTIONS = {
	INIT: 'INIT',
	LOGOUT: 'LOGOUT',
}

const INIT_FORM_DATA = {
	productId: '',
	currency: '',
	amount: '',
	quantity: '',
	country: '',
	customerEmail: '',
	businessEmail: '',
	referenceId: '',
};

const INIT_ERRORS = {
	productId: '',
	currency: '',
	amount: '',
	quantity: '',
	country: '',
	customerEmail: '',
	businessEmail: '',
	referenceId: '',
};

const GenerateVoucherModal = ({ isVisible, onClose, country, onCreate }) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
	const dashboard = useContext(DashboardEnvContext);

	const allProducts = useSelector(state => state.giftSortSku.products);
	const userEmail = useSelector(state => state.auth.email);
	const [errors, setErrors] = useState(INIT_ERRORS);
	const [formData, setFormData] = useState({ ...INIT_FORM_DATA, country: country, businessEmail: userEmail });
	const [selectedSku, setSelectedSku] = useState(null);
	const [hasError, setHasError] = useState(false);
	const [skuCountry, setSkuCountry] = useState(country);
	const [isLoading, setIsLoading] = useState(false);
	const [ssoAction, setSsoAction] = useState('');

	// auth modal
	const [isModalOpen, setIsModalOpen] = useState(false);

	useEffect(() => {
		if (!country) return;
		setFormData((prevState) => ({
			...prevState,
			country,
		}));
	}, [country]);

	useEffect(() => {
		if (!userEmail) return;
		setFormData((prevState) => ({
			...prevState,
			businessEmail: userEmail,
		}));
	}, [userEmail])

	useEffect(() => {
		dispatch(getAllProducts({ countryCode: skuCountry, categoryId: ALL_CATEGORY }));
		handleSelectedSKUChange(null);
	}, [skuCountry]);

	useEffect(() => {
		switch (ssoAction) {
			case SSO_ACTIONS.INIT:
				setIsModalOpen(true);
				break;

			case SSO_ACTIONS.LOGOUT:
				onAuthModalClose();
				setSsoAction(SSO_ACTIONS.INIT);
				break;
		
			default:
				setSsoAction('');
				break;
		}		
	}, [ssoAction])

	useEffect(() => {
		const keys = Object.keys(errors);
		let hasValue = true;
		let hasFormError = false;

		keys.forEach((errKey) => {
			if (formData[errKey] === '' || formData[errKey] === null) {
				hasValue = false;
			}
			if (errors[errKey]) {
				hasFormError = true;
			}
		});

		setHasError(!hasValue || hasFormError);
	}, [formData, errors]);

	const resetData = () => setFormData({ ...INIT_FORM_DATA, country, businessEmail: userEmail });

	const handleCloseModal = (e) => {
		e && e.stopPropagation();
		setErrors(INIT_ERRORS);
		resetData()
		onClose(false);
	};

	const validateField = (name, value) => {
		if (!(name in errors)) {
			return;
		}

		setErrors((prevState) => ({
			...prevState,
			[name]: value ? '' : 'This field is required.',
		}));
	};

	const validateHasError = (name, value) => {
		if (!(name in errors)) {
			return false;
		}

		return !(value !== null && value !== '' && value !== undefined);
	};

	const handleInputChange = (event, type) => {
		const { id, value } = event.target;

		if (type === 'number') {
			let number = 0;
			if (id === 'amount') {
				number = (Math.round(parseFloat(value) * 100) / 100).toString();
			} else {
				number = parseInt(value).toString();
			}

			setFormData((prevState) => ({
				...prevState,
				[id]: number,
			}));

			setErrors((prevState) => ({
				...prevState,
				[id]: value <= 0 ? 'Only accept positive numbers' : '',
			}));
			return;
		} else {

			setFormData((prevState) => ({
				...prevState,
				[id]: value,
			}));
		}

		if (['customerEmail', 'businessEmail'].includes(id)) {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			setErrors((prevState) => ({
				...prevState,
				[id]: !emailRegex.test(value) ? 'Please enter valid email address' : '',
			}));
			return;
		}

		validateField(id, value);
	};

	const inputPropsHandlers = {
		onChangeBlur: handleInputChange,
	};

	const handlSkuCountrySelectChange = (country) => {
		if (!country) return;
		setSkuCountry(country);
	};

	const handlCountrySelectChange = (country) => {
		if (!country) return;
		setFormData((prevState) => ({
			...prevState,
			country,
		}));
	};

	const handlCurrencySelectChange = (currency) => {
		if (!currency) return;
		setFormData((prevState) => ({
			...prevState,
			currency,
		}));
	};

	const handleSelectedSKUChange = (value) => {
		setSelectedSku(value);
		setFormData((prevState) => ({
			...prevState,
			productId: value ? value.id : ''
		}));
	}

	const onCreatePressHandler = () => {
		const items = Object.keys({ ...formData });
		let isAllFieldsValidated = true;

		items.forEach((item) => {
			validateField(item, formData[item]);
			if (validateHasError(item, formData[item])) {
				isAllFieldsValidated = false;
			}
		});

		if (isAllFieldsValidated) {
			setIsModalOpen(true);
		}
	}

	const onCreateHandler = (authPayload) => {
		const items = Object.keys({ ...formData });
		let isAllFieldsValidated = true;

		items.forEach((item) => {
			validateField(item, formData[item]);
			if (validateHasError(item, formData[item])) {
				isAllFieldsValidated = false;
			}
		});

		if (isAllFieldsValidated) {
			const payload = { ...formData };
			handleAddNew(payload, authPayload);
		}
	};

	const onAuthModalClose = () => {
		setIsModalOpen(false);
	}

	const onAuthModalSubmit = (payload) => {
		onCreateHandler(payload);
		setIsModalOpen(false);
	}

	const formatSubErrors = (subErrors) => `Error while trying to add a new bulk purchase:\n${subErrors.map((item) => `${item.field}: ${item.message}`).join("\n")}`;

	const handleAddNew = async (data, ssoAccessToken) => {
		setIsLoading(true);
		const result = await dispatch(addBulkPurchase({ data, ssoAccessToken }));

		if (addBulkPurchase.fulfilled.match(result)) {
			dashboard.utils.showSuccessNotification("Bulk purchase added successfully!");
			onCreate();
			handleCloseModal(false);
		} else {
			if (!!result.payload.subErrors) {
				dashboard.utils.showErrorNotification(formatSubErrors(result.payload.subErrors));
			} else if (!!result.payload.message && result.payload.message !== "") {
				dashboard.utils.showErrorNotification(`Error add bulk purchase: ${result.payload.message}`);
			} else {
				dashboard.utils.showErrorNotification("Error add bulk purchase");
			}
		}

		setIsLoading(false);
	};

	const onLoadingHandler = (loading) => {
		setIsLoading(loading);
	}

	const onAuthLoggedOut = () => {
		setSsoAction(SSO_ACTIONS.LOGOUT);
	}

	return (
		<Dialog
			open={isVisible}
			onClose={handleCloseModal}
			fullScreen={fullScreen}
			scroll="paper"
			maxWidth={'sm'}
		>
			<DialogTitle id="scroll-dialog-title" onClose={onClose}>
				New Bulk Purchase
			</DialogTitle>
			<DialogContent dividers>
				<Box sx={{ flexGrow: 1 }}>
					<Grid container spacing={3} columns={12} direction="column">
						<Grid item md={7} xs={12}>
							<Box sx={{ flexGrow: 1 }}>
								<Grid container spacing={3}>
									<Grid container item md={12} xs={12}>
										<CountryList
											onChange={handlSkuCountrySelectChange}
											placeholder="Select SKU Country"
											id="country"
											label="SKU Country"
											variant="filled"
											value={skuCountry || ''}
										/>
									</Grid>
									<Grid container item>
										<Grid item md={12} xs={12}>
											<SkuList
												options={allProducts || []}
												value={selectedSku}
												onSelectedOptionsChange={handleSelectedSKUChange}
											/>
										</Grid>
									</Grid>
									<Grid container item md={12} xs={12}>
										<BulkPurchaseCountryList
											onChange={handlCountrySelectChange}
											placeholder="Select Country"
											id="country"
											label="Country"
											variant="filled"
											value={formData.country != null ? formData.country : ''}
											error={!!errors.country}
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<CurrencyList
											onChange={handlCurrencySelectChange}
											placeholder="Select Currency"
											id="currency"
											label="Currency"
											variant="filled"
											value={formData.currency != null ? formData.currency : ''}
											error={!!errors.currency}
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Input
											{...inputPropsHandlers}
											id="quantity"
											label="Quantity"
											value={formData.quantity}
											error={errors.quantity}
											type='number'
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Input
											{...inputPropsHandlers}
											id="amount"
											label="Amount"
											value={formData.amount}
											error={errors.amount}
											type='number'
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Input
											{...inputPropsHandlers}
											id="referenceId"
											label="Reference No"
											value={formData.referenceId}
											error={errors.referenceId}
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Input
											{...inputPropsHandlers}
											id="customerEmail"
											label="Customer Email"
											value={formData.customerEmail}
											error={errors.customerEmail}
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Alert severity="warning">
											SSO account used to create vouchers must match with dashboard logged in business email.
										</Alert>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Input
											{...inputPropsHandlers}
											disabled
											id="businessEmail"
											label="Business Email"
											value={formData.businessEmail}
											error={errors.businessEmail}
										/>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCloseModal}>Cancel</Button>
				<Button
					variant="contained"
					onClick={onCreatePressHandler}
					disabled={hasError}
				>
					Create
				</Button>
			</DialogActions>

			{isModalOpen &&
				<BulkPurchaseAuth
					isVisible={isModalOpen}
					onClose={onAuthModalClose}
					onSubmit={onAuthModalSubmit}
					userEmail={userEmail}
					loadingHandler={onLoadingHandler}
					onLoggedOut={onAuthLoggedOut}
				/>
			}

			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
				open={isLoading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
		</Dialog>
	);
};

export default GenerateVoucherModal;
